<script setup lang="ts">
/**
 * DesktopMain — three-column content area used on md+ viewports.
 *
 * InfoViewLayout and ConversationLayout are fixed-width flanks; the middle
 * column is held at --width-middle-column so the table geometry stays
 * constant across tabs. Extra horizontal space (e.g. from a collapsed
 * sidebar) is absorbed by the two flanks via flex-grow.
 *
 * The non-scoped <style> below overrides Tufte's <article> defaults so the
 * middle column fills its container rather than the full page width.
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import InfoViewLayout from '@/components/InfoViewLayout.vue'
import ConversationLayout from '@/components/ConversationLayout.vue'

/**
 * Finances renders a table with a data-driven (potentially large) number of
 * columns, unlike every other tab's fixed ~5-column layout. The right
 * conversation flank is dropped entirely for that route; InfoViewLayout is
 * left untouched (`flex: 1 1 0`, same as every other tab) so it keeps
 * exactly the width it always has, and the middle column picks up
 * ConversationLayout's former share instead — see `.middle-column--wide`.
 */
const route = useRoute()
const isWideTab = computed(() => route.name === 'Finances')
</script>

<template>
  <!--
    h-full stretches the row to the <main> height so each column can
    manage its own vertical overflow independently.
  -->
  <div class="flex h-full">
    <InfoViewLayout />
    <div
      class="middle-column min-h-0 min-w-0 overflow-auto"
      :class="{ 'middle-column--wide': isWideTab }"
    >
      <div class="p-4">
        <RouterView />
      </div>
    </div>
    <ConversationLayout v-if="!isWideTab" />
  </div>
</template>

<!--
  Non-scoped: <article> is rendered by routed tab views (children outside
  this component's scoped-style reach). Overrides Tufte defaults (width:
  87.5%, padding-left: 12.5%, vertical padding 5rem) which are sized for a
  full-page essay and would otherwise force the middle column wide or add a
  large empty left gutter.
-->
<style>
.middle-column {
  flex: 0 0 var(--width-middle-column);
  max-width: 100%;
}

/*
 * Matching InfoViewLayout's flex-grow: 1 (instead of the default 0) means
 * the two remaining flex items split the leftover space 50/50, exactly as
 * InfoViewLayout and ConversationLayout did before ConversationLayout was
 * removed from the layout — InfoViewLayout's rendered width is therefore
 * unchanged, and the half that used to go to ConversationLayout now goes
 * to the middle column instead.
 */
.middle-column--wide {
  flex: 1 1 var(--width-middle-column);
}

.middle-column article {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2rem 1rem;
  box-sizing: border-box;
}

.middle-column article h1 {
  margin-top: 0;
}
</style>
